import { Viewer } from '../Viewer';
import * as THREE from 'three';
import { ViewerScene } from '../ViewerScene';
import { ViewerLayersBit } from '../Enums/ViewerLayers';
import { SVGOverlay } from '../Overlays/SVGOverlay';
import { HsbVisual } from '../../hsb-visual';
import {
    Edge,
    List,
    VertexEdge,
    VertexList,
    VertexNode,
} from '../Helpers/VertexList';
import {  Triangle, Vector3 } from 'three';
import * as BufferGeometryUtils from "three/examples/jsm/utils/BufferGeometryUtils"
import { VertexFace } from '../Helpers/Face';
import { ConvexGeometry } from 'three/examples/jsm/geometries/ConvexGeometry';

export enum SnapGeometry {
    NONE = 0,
    VERTEX = 1,
    EDGE = 2,
    VERTEXEDGE = 3,
    FACE = 4,
    VERTEXFACE = 5,
    EDGEFACE = 6,
    VERTEXEDGEFACE = 7,
}

export type faceInfo = {
    vertices: THREE.Vector3[];
    center: THREE.Vector3;
    normal: THREE.Vector3;
    paths: VertexList[];
};

type QuantityFlag = true | false | boolean;
type QuantityType<T, K> = T extends true ? K : T extends false ? K[] : never;

type BaseSnapType<T> = {
    intersection: QuantityType<T, THREE.Intersection>;
    object: QuantityType<T, THREE.Mesh>;
    normal: QuantityType<T, THREE.Vector3>;
    mode: SnapGeometry;
    point: THREE.Vector3;
};
type VertexSnapType = { vertex: THREE.Vector3 };
type EdgeSnapType = { edge: THREE.Line3 };
type FaceSnapType = { face: faceInfo };
type SnapType<T, K extends false | true> = T extends SnapGeometry.NONE
    ? BaseSnapType<K>
    : T extends SnapGeometry.VERTEX
    ? BaseSnapType<true> & VertexSnapType
    : T extends SnapGeometry.EDGE
    ? BaseSnapType<true> & EdgeSnapType
    : T extends SnapGeometry.VERTEXEDGE
    ? BaseSnapType<true> & EdgeSnapType & VertexSnapType
    : T extends SnapGeometry.FACE
    ? BaseSnapType<true> & FaceSnapType
    : T extends SnapGeometry.VERTEXFACE
    ? BaseSnapType<true> & VertexSnapType & FaceSnapType
    : T extends SnapGeometry.EDGEFACE
    ? BaseSnapType<true> & EdgeSnapType & FaceSnapType
    : T extends SnapGeometry.VERTEXEDGEFACE
    ? BaseSnapType<true> & VertexSnapType & EdgeSnapType & FaceSnapType
    : never;

/**
 * The picker performs all raycasts and also performs snapping if requested.
 */
export class Picker {
    _raycaster: THREE.Raycaster;
    _raycasts: { [key: number]: any };

    public set DefaultSnappingTreshold(treshold: number) {
        this._defaultSnappingTreshold = treshold;
    }
    public get DefaultSnappingTreshold() {
        return this._defaultSnappingTreshold;
    }
    private _defaultSnappingTreshold: number = 1000;

    constructor(private _viewer: Viewer) {
        this._raycaster = new THREE.Raycaster();
        this._raycasts = {};
    }

    /**
     * Request a raycast to be performed. Returns a new raycast result if necessary or the result of a raycast with the same params performed in this frame.
     * @param coords The (2d-)coords in screenspace [-1,1] for which to perform the raycast
     * @param {Partial<{ camera: THREE.Camera, root: ViewerScene, layer: ViewerLayers, recursive: boolean, onlyReceiveFirst: boolean, snapGeometry: SnapGeometry }>} params Additional params for the raycast.
     */
    requestRaycast<
        K extends QuantityFlag = true,
        T extends SnapGeometry = SnapGeometry.NONE
    >(
        coords: { x: number; y: number },
        params: {
            camera?: THREE.Camera;
            root?: ViewerScene;
            layer?: ViewerLayersBit;
            sheetingZones?: number[];
            snappingTreshold?: number;
            recursive?: boolean;
            onlyReceiveFirst?: K;
            snapGeometry?: T;
        }
    ): SnapType<T, K> | null | undefined {
        let parameters = params || {};
        let target = parameters.root || this._viewer.currentScene;
        if (!target) {
            console.warn(
                'Picker::requestRaycast(): No target set for raycast! Cannot pick for undefined scene.'
            );
            return null;
        }
        let targetCamera = parameters.camera || this._viewer.currentCamera;
        if (!targetCamera) return null;
        let layer = parameters.layer ?? ViewerLayersBit.ALL;
        layer &= targetCamera.layers.mask;
        let onlyReceiveFirst =
            parameters.onlyReceiveFirst !== undefined
                ? parameters.onlyReceiveFirst
                : true;
        let snapGeometry = parameters.snapGeometry || SnapGeometry.NONE;
        let treshold = parameters.snappingTreshold;

        // If not we want to perform the raycast and index the results before returning it
        let recursive = params?.recursive ? params.recursive : true;
        if (layer) this._raycaster.layers.mask = layer;
        else this._raycaster.layers.enableAll();

        try {
            this._raycaster.setFromCamera(new THREE.Vector2(coords.x,coords.y), targetCamera);
            let intersections: THREE.Intersection[] =
                this._raycaster.intersectObject(target, recursive);

            // Filter out objects that have hidden visual
            intersections = intersections?.filter((intersection) => {
                let obj = intersection.object;
                let instanceId = intersection.instanceId;
                if (instanceId === undefined) {
                    return (
                        obj.type !== 'TransformControlsPlane' &&
                        obj.userData.visible
                    );
                } else {
                    return (
                        obj.type !== 'TransformControlsPlane' &&
                        obj.userData.visible[instanceId]
                    );
                }
            });

            // No hits => return null
            if (!intersections || intersections.length === 0) return null;

            // Issues
            let issueSprites = intersections.filter(
                (i) => (i.object.layers.mask & ViewerLayersBit.ISSUES) === ViewerLayersBit.ISSUES
            );
            if (issueSprites.length > 0) {
                let closestIssue = issueSprites[0];
                return {
                    object: closestIssue.object,
                    mode: SnapGeometry.NONE,
                    intersection: [closestIssue],
                    normal: closestIssue.face?.normal,
                    point: closestIssue.object.getWorldPosition(
                        new THREE.Vector3()
                    ),
                } as any;
            }

            // Check if a snap is desired
            if (snapGeometry !== SnapGeometry.NONE) {
                return this.snap(
                    snapGeometry,
                    intersections[0],
                    treshold
                ) as any;
            } else {
                try {
                    let intersection = onlyReceiveFirst
                        ? intersections[0]
                        : intersections;
                    let object = onlyReceiveFirst
                        ? intersections[0].object
                        : intersections.map((i) => i.object);
                    let normal = onlyReceiveFirst
                        ? intersections[0].face?.normal
                        : intersections.map((i) => i.face?.normal);
                    return {
                        object: object,
                        intersection: intersection,
                        mode: SnapGeometry.NONE,
                        normal: intersections[0].face?.normal,
                        point: intersections[0].point,
                    } as any;
                } catch (err) {
                    console.error(err);
                    throw err;
                }
            }
        } catch (error) {
            console.error(error);
            return null;
        }
    }

    /**
     * Performs the snapping for the given intersection depending on the snapping mode
     * @param intersection The intersectionf or which to perform the snapping.
     */
    snap<T extends SnapGeometry = SnapGeometry.NONE, K extends boolean = false>(
        snapGeometry: T,
        intersection: THREE.Intersection,
        treshold?: number
    ): SnapType<T, K> | null {
        if (!intersection) return null;
        let snapped = false;
        let out: any = {};
        out['object'] = intersection.object;
        out['intersection'] = intersection;
        if ((snapGeometry & SnapGeometry.VERTEX) === SnapGeometry.VERTEX) {
            let mesh = intersection.object;
            if (mesh instanceof THREE.Mesh) {
                let vertices = mesh.geometry.getAttribute('position');
                let normals = mesh.geometry.getAttribute('normal');
                // Get local pos of the points

                let a = {
                    pos: new THREE.Vector3().fromBufferAttribute(
                        vertices,
                        intersection.face!.a
                    ),
                    normal: new THREE.Vector3().fromBufferAttribute(
                        normals,
                        intersection.face!.a
                    ),
                };
                let b = {
                    pos: new THREE.Vector3().fromBufferAttribute(
                        vertices,
                        intersection.face!.b
                    ),
                    normal: new THREE.Vector3().fromBufferAttribute(
                        normals,
                        intersection.face!.b
                    ),
                };
                let c = {
                    pos: new THREE.Vector3().fromBufferAttribute(
                        vertices,
                        intersection.face!.c
                    ),
                    normal: new THREE.Vector3().fromBufferAttribute(
                        normals,
                        intersection.face!.c
                    ),
                };
                // Get world pos and world normal
                let points = [a, b, c].map((point) => {
                    if (mesh instanceof THREE.InstancedMesh) {
                        let m4 = new THREE.Matrix4();
                        mesh.getMatrixAt(intersection.instanceId!, m4);
                        point.pos.applyMatrix4(m4);
                        // point.normal.applyMatrix4(m4);
                    }

                    point.pos = mesh.localToWorld(point.pos);
                    var normalMatrix = new THREE.Matrix3().getNormalMatrix(
                        mesh.matrixWorld
                    );
                    point.normal.applyMatrix3(normalMatrix).normalize();

                    return point;
                });

                // Sort by distance
                points.sort(
                    (a, b) =>
                        a.pos.distanceToSquared(intersection.point) -
                        b.pos.distanceToSquared(intersection.point)
                );
                // Treshold test
                if (
                    !treshold ||
                    intersection.point.distanceTo(points[0].pos) <= treshold
                ) {
                    // Overlay
                    this._viewer.SVGOverlay.createSnappingOverlay(
                        { vertex: points[0].pos },
                        0x00ff00,
                        SnapGeometry.VERTEX
                    );
                    out['point'] = points[0].pos;
                    out['normal'] = points[0].normal;
                    out['mode'] = SnapGeometry.VERTEX;
                    snapped = true;
                    return out;
                }
            }
        }
        var faces;
        if ((snapGeometry & SnapGeometry.EDGE) === SnapGeometry.EDGE) {
            let mesh = intersection.object;
            if (mesh instanceof THREE.Mesh) {
                // Getting data for this face + faces that are alligned to it
                faces = this.getNeighbourFaces(
                    mesh,
                    intersection.face!,
                    intersection.point,
                    intersection.faceIndex!,
                    intersection.instanceId
                );
                let lines: THREE.Line3[] = [];
                for (let i = 0; i < faces.vertices.length; ++i) {
                    lines.push(
                        new THREE.Line3(
                            faces.vertices[i],
                            faces.vertices[i + (1 % faces.vertices.length)]
                        )
                    );
                }
                // Sort by distance from cloest point on line to intersection point
                lines.sort((a, b) => {
                    return (
                        intersection.point.distanceToSquared(
                            a.closestPointToPoint(
                                intersection.point,
                                false,
                                new THREE.Vector3()
                            )
                        ) -
                        intersection.point.distanceToSquared(
                            b.closestPointToPoint(
                                intersection.point,
                                false,
                                new THREE.Vector3()
                            )
                        )
                    );
                });
                let closestPoint = lines[0].closestPointToPoint(
                    intersection.point,
                    false,
                    new THREE.Vector3()
                );
                if (
                    !treshold ||
                    intersection.point.distanceTo(closestPoint) <= treshold
                ) {
                    // Overlay

                    this._viewer.SVGOverlay.createSnappingOverlay(
                        { vertex: closestPoint, edge: lines[0] },
                        0x00ff00,
                        SnapGeometry.EDGE
                    );
                    out['edge'] = lines[0];
                    var normalMatrix = new THREE.Matrix3().getNormalMatrix(
                        mesh.matrixWorld
                    );
                    out['normal'] = intersection.face?.normal
                        .clone()
                        .applyMatrix3(normalMatrix)
                        .normalize();
                    out['mode'] = SnapGeometry.EDGE;
                    out['object'] = mesh;
                    out['point'] = closestPoint;
                    snapped = true;
                    return out;
                }
            }
        }
        if ((snapGeometry & SnapGeometry.FACE) === SnapGeometry.FACE) {
            let mesh = intersection.object;
            if (mesh instanceof THREE.Mesh) {
                // Getting data for this face + faces that are alligned to it
                if (!faces) {
                    faces = this.getNeighbourFaces(
                        mesh,
                        intersection.face!,
                        intersection.point,
                        intersection.faceIndex!,
                        intersection.instanceId
                    );
                }
                // Overlay
                this._viewer.SVGOverlay.createSnappingOverlay(
                    { face: faces, vertex: intersection.point },
                    0x00ff00,
                    SnapGeometry.FACE
                );

                out['face'] = faces;
                out['normal'] = faces.normal;
                out['mode'] = SnapGeometry.FACE;
                out['object'] = mesh;
                out['point'] = intersection.point;
                snapped = true;
                return out;
            }
        }
        if (!snapped) {
            let mesh = intersection.object;
            if (mesh instanceof THREE.Mesh) {
                out['mode'] = SnapGeometry.NONE;
                out['object'] = mesh;
                out['normal'] = intersection.face?.normal;
                if (!faces) {
                    faces = this.getNeighbourFaces(
                        mesh,
                        intersection.face!,
                        intersection.point,
                        intersection.faceIndex!,
                        intersection.instanceId
                    );
                }
                out['face'] = faces;
                out['point'] = intersection.point;
                this._viewer.SVGOverlay.createSnappingOverlay(
                    { vertex: intersection.point },
                    0x00ff00,
                    SnapGeometry.VERTEX
                );
            }
        }
        return out;
    }

    /**
     * Performs an intersect for the given coords and plane. Returns the intersection point if possible, else null.
     * @param plane The plane on which to perform the raycast
     * @param coords The (2d-)coords in screnspace [-1,1]for which to perform the raycast.
     * @param camera
     */
    requestPlaneIntersect(
        plane: THREE.Plane,
        coords: { x: number; y: number },
        camera?: THREE.Camera
    ): THREE.Vector3 | null {
        let result = new THREE.Vector3();
        let targetCamera = camera ? camera : this._viewer.currentCamera;
        if (!targetCamera) return null;
        this._raycaster.setFromCamera(new THREE.Vector2(coords.x,coords.y), targetCamera);
        if (this._raycaster.ray.intersectPlane(plane, result)) return result;

        return null;
    }

    requestRay(
        coords: { x: number; y: number },
        camera: THREE.Camera
    ): THREE.Ray {
        this._raycaster.setFromCamera(new THREE.Vector2(coords.x,coords.y), camera);
        return this._raycaster.ray.clone();
    }

    update() {
        this._raycasts = {};
    }

    /**
     * Test and returns the neighbouring faces if they are part of the given face's surface.
     * @param mesh The mesh to which the faces belong.
     * @param faceNormal The normal of the given face
     * @param faceIndex The index of the given face.
     */
    getNeighbourFaces(
        mesh: THREE.Mesh,
        face: THREE.Face,
        point: THREE.Vector3,
        faceIndex: number,
        instanceId?: number
    ): faceInfo {
        let geometry = mesh.geometry as THREE.BufferGeometry;
        let triangles: Triangle[] = [];
        let positions = geometry.getAttribute('position');
        let normals = geometry.getAttribute('normal');
        let vertices: any[] = [];
        // Setting up outline
        let index = 0;
        var facePoint = new THREE.Vector3().fromBufferAttribute(
            positions,
            face.a
        );
        let plane = new THREE.Plane().setFromNormalAndCoplanarPoint(
            face.normal,
            facePoint
        );
        // Check the prior face
        while (index < positions.count) {
            var a = new THREE.Vector3().fromBufferAttribute(normals, index * 3);
            var b = new THREE.Vector3().fromBufferAttribute(
                normals,
                index * 3 + 1
            );
            var c = new THREE.Vector3().fromBufferAttribute(
                normals,
                index * 3 + 2
            );
            let normal = new THREE.Vector3()
                .add(a)
                .add(b)
                .add(c)
                .divideScalar(3);
            if (normal.dot(face.normal) > 0.999999) {
                var a = new THREE.Vector3().fromBufferAttribute(
                    positions,
                    index * 3
                );
                let dist = plane.distanceToPoint(a);
                if (Math.abs(dist) < Number.EPSILON) {
                    var b = new THREE.Vector3().fromBufferAttribute(
                        positions,
                        index * 3 + 1
                    );
                    var c = new THREE.Vector3().fromBufferAttribute(
                        positions,
                        index * 3 + 2
                    );
                    let triangle = new THREE.Triangle(a, b, c);
                    triangles.push(triangle);
                    vertices.push(a, b, c);
                }
            }
            index++;
        }
        // Local to world
        var normalMatrix = new THREE.Matrix3().getNormalMatrix(
            mesh.matrixWorld
        );
        let worldNormal: THREE.Vector3;
        if (mesh instanceof THREE.InstancedMesh) {
            let m4 = new THREE.Matrix4();
            mesh.getMatrixAt(instanceId!, m4);
            vertices = vertices.map((v) => {
                v = v.applyMatrix4(m4);
                v = new THREE.Vector3(v.x, v.z, -v.y);
                return v;
            });
            triangles.forEach((t) => {
                // t.a = t.a.applyMatrix4(m4);
                t.a = new THREE.Vector3(t.a.x, t.a.z, -t.a.y);
                // t.b = t.b.applyMatrix4(m4);
                t.b = new THREE.Vector3(t.b.x, t.b.z, -t.b.y);
                // t.c = t.c.applyMatrix4(m4);
                t.c = new THREE.Vector3(t.c.x, t.c.z, -t.c.y);
            });
            let instanceRot = new THREE.Matrix4().extractRotation(m4);
            let worldRot = new THREE.Matrix4().extractRotation(
                mesh.matrixWorld
            );
            worldNormal = face.normal
                .clone()
                .applyMatrix4(instanceRot)
                .applyMatrix4(worldRot)
                .normalize();
        } else {
            worldNormal = face.normal
                .clone()
                .applyMatrix3(normalMatrix)
                .normalize();
            vertices = vertices.map((vertex) => mesh.localToWorld(vertex));
        }
        let center = vertices
            .reduce((a, b) => a.clone().add(b))
            .divideScalar(vertices.length);
        // Get paths
        let paths = this.getPaths(triangles);
        return {
            normal: worldNormal,
            vertices: vertices,
            center: center,
            paths: paths,
        };
    }

    getPaths(triangles: THREE.Triangle[]): VertexList[] {
        // Get all edges
        let edges: VertexEdge[] = triangles.reduce((accu: any[], triangle) => {
            let ab = VertexEdge.Create(triangle.a, triangle.b);
            let bc = VertexEdge.Create(triangle.b, triangle.c);
            let ca = VertexEdge.Create(triangle.c, triangle.a);
            accu.push(ab, bc, ca);
            return accu;
        }, []);
        let dupes: number[] = [];
        // Check if edges are duplicates
        edges.forEach((edge, index) => {
            let currDupes = edges
                .map((edge2, index2) => {
                    // ALready know its duped
                    if (dupes.includes(index2)) return -1;
                    // Dont check itself
                    // Do check if any other edge is equal or twin
                    if (
                        index != index2 &&
                        (edge.equals(edge2) || edge.isTwin(edge2))
                    )
                        return index2;
                    else return -1;
                })
                .filter((index) => index > -1); // Dont need the -1
            if (currDupes.length > 0) currDupes.unshift(index);
            // pUsh to collection of duped indeces
            dupes.push(...currDupes);
        });
        // Sort by index for optimization
        dupes = dupes.sort((a, b) => a - b);
        // Filter all dupes out
        edges = edges.filter((edge, index) => {
            let res = dupes.findIndex((i) => i === index);
            if (res > -1) dupes.splice(res, 1);
            return !(res > -1);
        });
        // Construct possible paths
        let tempEdges = Array.from(edges);
        let shapes: VertexList[] = [];
        // Iterate to construct the path of edges that are connected
        while (tempEdges.length > 0) {
            let curr = tempEdges.shift();
            if (!curr) break;
            // Create new path
            let out = new VertexList(curr.start, curr.end);
            let i = 0;
            while (i < tempEdges.length) {
                const edge = tempEdges[i];
                // Check if the edge is connected to this list
                if (edge.start.equals(out.head)) {
                    out.unshift(edge.end);
                    tempEdges.splice(i, 1);
                    i = 0;
                    continue;
                } else if (edge.start.equals(out.tail)) {
                    out.append(edge.end);
                    tempEdges.splice(i, 1);
                    i = 0;
                    continue;
                } else if (edge.end.equals(out.head)) {
                    out.unshift(edge.start);
                    tempEdges.splice(i, 1);
                    i = 0;
                    continue;
                } else if (edge.end.equals(out.tail)) {
                    out.append(edge.start);
                    tempEdges.splice(i, 1);
                    i = 0;
                    continue;
                }
                ++i;
            }
            // pUsh finsihed path
            shapes.push(out);
        }
        // Return all paths
        return shapes;
    }

    addToShape(face: VertexFace, values: Vector3[]): VertexFace {
        let nodes = values.map((v) => face.createNode(v));
        let hitList = face.lists.filter((l) =>
            nodes.some((node) => l.contains(node))
        );
        if (hitList.length === 0) {
            let list = new VertexList(nodes[0], nodes[1]);
            list.append(nodes[2]);
            face.lists.push(list);
            return face;
        }
        let edges = nodes.map((n, index) => {
            return new VertexEdge(n, nodes[(index + 1) % nodes.length]);
        });
        hitList.forEach((list) => {
            let hitEdges = edges.filter((nE) => {
                return list.edges.some(
                    (e) => e.equals(nE) || e.equals(nE.twin)
                );
            });
            switch (hitEdges.length) {
                case 0: {
                    let newList = new VertexList(nodes[0], nodes[1]);
                    newList.append(nodes[2]);
                    face.lists.push(newList);
                    break;
                }
                case 1: {
                    let s = hitEdges[0].start;
                    let e = hitEdges[0].end;
                    let preNode = list.find((n) => n.id === s.id);
                    let toAdd = nodes.find(
                        (vn) => vn.id !== s.id && vn.id !== e.id
                    );
                    if (preNode && toAdd) list.insertAfter(preNode, toAdd);
                    break;
                }
            }
        });

        return face;
    }
    equals(
        lhs: { x: number; y: number; z: number },
        rhs: { x: number; y: number; z: number },
        epsilon = Number.EPSILON
    ) {
        return (
            Math.abs(lhs.x - rhs.x) < epsilon &&
            Math.abs(lhs.y - rhs.y) < epsilon &&
            Math.abs(lhs.z - rhs.z) < epsilon
        );
    }
}
